/**
 * ADRIA WINE — post add to cart upsell enhancement.
 *
 * The panel itself is server rendered Liquid and is complete without this file. Everything
 * here is progressive enhancement, so a parse error, a blocked asset, or an old browser
 * costs the shopper nothing. There is no network request on any path: add to cart can
 * never be delayed, blocked, or left in a wrong state by this script.
 *
 * How it detects an add
 * ---------------------
 * It does NOT guess at a cart event name. Nothing in this theme's Liquid dispatches a
 * documented cart CustomEvent, and assets/events.js was not available to read, so a
 * `cart:update` listener would be a guess. What IS provable from the theme source is the
 * header cart bubble: snippets/cart-bubble.liquid renders
 *   <span class="cart-bubble__text-count" ref="cartBubbleCount" data-testid="cart-bubble">
 * inside <header-actions> (snippets/header-actions.liquid), and the theme updates that
 * number on every successful add, from any entry point — the main buy button, the sticky
 * add to cart bar, quick add, and the cards in this panel. So the count is the signal.
 *
 * A MutationObserver scoped to <header-actions> catches the change. Two provable document
 * events, `theme-drawer:open` and `theme-drawer:close` (snippets/theme-drawer.liquid),
 * plus pageshow, trigger a re-read and a re-attach, so the script recovers if the header
 * is ever replaced wholesale and a mutation is missed.
 */

const PANEL_SELECTOR = '[data-adria-upsell="pdp"]';
const CARD_SELECTOR = '[data-adria-upsell-card]';
const COUNT_SELECTORS = [
  '[data-testid="cart-bubble"]',
  '[ref="cartBubbleCount"]',
  '.cart-bubble__text-count',
];

const ADDED_HOLD_MS = 1100;
const REMOVE_FADE_MS = 320;
const PENDING_TTL_MS = 6000;

let panel = null;
let lastCount = null;
let pendingCard = null;
let pendingTimer = 0;
let headingSwapped = false;
const observed = new WeakSet();
const boundPanels = new WeakSet();
let observer = null;

function reducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Reads the header cart bubble count. Returns a number, or null when unreadable. */
function readCount() {
  for (const selector of COUNT_SELECTORS) {
    const node = document.querySelector(selector);
    if (!node) continue;
    const text = (node.textContent || '').trim();
    if (text === '') continue;
    const value = Number.parseInt(text.replace(/[^0-9]/g, ''), 10);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

/** Attaches the observer to every header-actions element currently in the document. */
function attachObservers() {
  if (!observer) return;

  let targets = Array.from(document.querySelectorAll('header-actions'));
  if (targets.length === 0) {
    const bubble = document.querySelector('.cart-bubble');
    const fallback = (bubble && bubble.closest('header')) || document.querySelector('header');
    if (fallback) targets = [fallback];
  }

  for (const target of targets) {
    if (observed.has(target)) continue;
    observed.add(target);
    observer.observe(target, { childList: true, subtree: true, characterData: true });
  }
}

function clearPending() {
  pendingCard = null;
  if (pendingTimer) {
    window.clearTimeout(pendingTimer);
    pendingTimer = 0;
  }
}

/** Hides the whole panel once every card has been taken. */
function hidePanelIfEmpty() {
  if (!panel) return;
  if (panel.querySelector(CARD_SELECTOR)) return;
  panel.hidden = true;
}

/**
 * The shopper added this exact suggestion, so it must stop being suggested. The card is
 * covered with its own pre-rendered 「追加しました」 label, then removed. No markup that
 * the theme's own add to cart animation owns is touched.
 */
function retireCard(card) {
  if (!card || card.hasAttribute('data-adria-added')) return;
  card.setAttribute('data-adria-added', '');

  const item = card.closest('.adria-upsell__item') || card;
  const hold = reducedMotion() ? 400 : ADDED_HOLD_MS;

  window.setTimeout(() => {
    item.setAttribute('data-adria-removing', '');
    window.setTimeout(() => {
      if (item.parentNode) item.parentNode.removeChild(item);
      hidePanelIfEmpty();
    }, reducedMotion() ? 0 : REMOVE_FADE_MS);
  }, hold);
}

/** Swaps the panel heading to the post add wording. Runs at most once per page view. */
function swapHeading() {
  if (headingSwapped || !panel) return;

  const heading = panel.querySelector('[data-adria-upsell-heading]');
  const added = heading && heading.getAttribute('data-adria-upsell-heading-added');
  if (heading && added) heading.textContent = added;

  const sub = panel.querySelector('[data-adria-upsell-sub]');
  if (sub) sub.hidden = false;

  panel.setAttribute('data-adria-upsell-state', 'added');
  headingSwapped = true;
}

function bringIntoView() {
  if (!panel || panel.hidden) return;
  // Pointless while the cart drawer is covering the page.
  if (document.querySelector('theme-drawer[open]')) return;

  const rect = panel.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const alreadyVisible = rect.top < viewportHeight - 60 && rect.bottom > 60;
  if (alreadyVisible) return;

  panel.scrollIntoView({
    behavior: reducedMotion() ? 'auto' : 'smooth',
    block: 'center',
  });
}

function onCartGrew() {
  if (!panel || panel.hidden) return;

  const card = pendingCard;
  clearPending();

  swapHeading();

  if (card && card.isConnected) {
    retireCard(card);
    return;
  }

  bringIntoView();
}

/** Re-reads the count, re-attaches observers, and reacts if the cart grew. */
function sync() {
  try {
    syncInner();
  } catch (error) {
    /* Enhancement only. Never let a DOM surprise here surface to the shopper. */
  }
}

function syncInner() {
  ensurePanel();
  attachObservers();

  const count = readCount();
  if (count === null) return;

  if (lastCount === null) {
    lastCount = count;
    return;
  }

  if (count > lastCount) {
    lastCount = count;
    onCartGrew();
    return;
  }

  lastCount = count;
}

/**
 * Keeps the panel reference valid. Horizon re-renders a section over the Section
 * Rendering API and morphs the result back in, which can replace this node wholesale,
 * so the reference is re-taken and the listener re-bound whenever that happens.
 */
function ensurePanel() {
  if (panel && panel.isConnected) return;
  const found = document.querySelector(PANEL_SELECTOR);
  if (!found) return;
  panel = found;
  bindPanel(panel);
}

function bindPanel(node) {
  if (boundPanels.has(node)) return;
  boundPanels.add(node);

  // Records which suggestion was submitted, so the right card can be retired when the
  // count moves. Capture phase and read only: it never calls preventDefault and never
  // touches the event, so the theme's own submit handling is untouched.
  node.addEventListener(
    'submit',
    (event) => {
      try {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        const card = target.closest(CARD_SELECTOR);
        if (!card) return;

        pendingCard = card;
        if (pendingTimer) window.clearTimeout(pendingTimer);
        pendingTimer = window.setTimeout(clearPending, PENDING_TTL_MS);
      } catch (error) {
        /* Never interfere with the submit that is in flight. */
      }
    },
    true
  );
}

function init() {
  panel = document.querySelector(PANEL_SELECTOR);
  if (!panel) return;

  bindPanel(panel);
  lastCount = readCount();

  observer = new MutationObserver(() => sync());
  attachObservers();

  document.addEventListener('theme-drawer:open', sync);
  document.addEventListener('theme-drawer:close', sync);
  window.addEventListener('pageshow', sync);
}

function safeInit() {
  try {
    init();
  } catch (error) {
    /* Enhancement only. The server rendered panel stands on its own. */
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeInit, { once: true });
} else {
  safeInit();
}
